import type { FilterOp, FrontendFilter } from "../../Dashboard/types";

// AND/OR filter joins (#10851).
//
// A clause's `join` describes how it binds to the clause BEFORE it in the
// ordered list: "or" extends the previous OR group, anything else starts
// a new group; groups AND together. The backend applies the same fold.
//
// A payload where NO clause carries a join is a legacy payload: the
// backend groups by attribute name (same-name clauses OR, different
// names AND). The helpers here upgrade a legacy list to explicit joins
// the moment the user edits structure (adds a clause, toggles a
// connector, drags a chip) — and reproduce the legacy grouping exactly
// when they do, so the meaning of a saved view never changes out from
// under the user.

export type FilterJoin = "and" | "or";

const hasExplicitJoins = (filters: FrontendFilter[]): boolean =>
  filters.some((f) => f.join !== undefined);

// The clause fields that go over the wire — everything else on a
// FrontendFilter is display-only state. ONE definition, shared by the
// search/count body builder (useTableData) and the export body builder
// (exportTable): the two previously carried their own copies, and a
// field added to one but not the other makes the export silently query
// a different row set than the table it claims to export. `join` and
// `group` carry the AND/OR expression (#10851).
export interface WireFilter {
  name: string;
  op: FilterOp;
  value: FrontendFilter["value"];
  component?: string;
  join?: FilterJoin;
  group?: number;
  negate?: true;
}

export const stripDisplayFields = (filters: FrontendFilter[]): WireFilter[] => {
  return filters.map(({ name, op, value, component, join, group, negate }) => {
    let wireValue = value;
    // A `near` clause's value carries the geocoder's display label;
    // only the coordinates and radius belong on the wire.
    if (
      op === "near" &&
      value &&
      typeof value === "object" &&
      "label" in value
    ) {
      const { label: _label, ...rest } = value;
      void _label;
      wireValue = rest;
    }
    return {
      name,
      op,
      value: wireValue,
      ...(component !== undefined ? { component } : {}),
      ...(join !== undefined ? { join } : {}),
      ...(group !== undefined ? { group } : {}),
      ...(negate ? { negate } : {}),
    };
  });
};

// ── top-level OR groups (#10851) ────────────────────────────────
//
// One added precedence level: clauses sharing a `group` index form one
// AND-of-ORs expression (the join model below, verbatim) and the groups
// OR together. `group` is only written while a second group exists, so
// a single-group list is byte-identical to a pre-group list and legacy
// saved views never change shape until the user actually makes a group.

// Partition clause indexes by group in first-appearance order.
export const orGroupPartitions = (filters: FrontendFilter[]): number[][] => {
  const order: number[] = [];
  const byGroup = new Map<number, number[]>();
  filters.forEach((f, i) => {
    const key = f.group ?? 0;
    if (!byGroup.has(key)) {
      order.push(key);
      byGroup.set(key, []);
    }
    byGroup.get(key)!.push(i);
  });
  return order.map((key) => byGroup.get(key)!);
};

// Canonical group shape: partitions contiguous in first-appearance
// order, indexes dense from 0 — or no `group` fields at all when only
// one group remains. Returns the input untouched when already canonical
// (so a legacy list stays `===` and is never rewritten by rendering).
export const normalizeOrGroups = (
  filters: FrontendFilter[]
): FrontendFilter[] => {
  const partitions = orGroupPartitions(filters);
  if (partitions.length <= 1) {
    if (filters.every((f) => f.group === undefined)) return filters;
    return filters.map(({ group: _group, ...rest }) => rest);
  }
  const canonical = partitions.every(
    (partition, gi) =>
      partition.every((i) => filters[i].group === gi) &&
      (gi === 0 || partition[0] > partitions[gi - 1].at(-1)!)
  );
  if (canonical) return filters;
  return partitions.flatMap((partition, gi) =>
    partition.map((i) => ({ ...filters[i], group: gi }))
  );
};

// Move the clause at `index` into its own new group at the end of the
// strip — the "OR this with everything else" gesture. No-op when the
// clause already is the last group by itself.
export const moveClauseToNewGroup = (
  filters: FrontendFilter[],
  index: number
): FrontendFilter[] => {
  const normalized = normalizeOrGroups(normalizeJoins(filters));
  if (index < 0 || index >= normalized.length) return normalized;
  const partitions = orGroupPartitions(normalized);
  const last = partitions.at(-1)!;
  if (last.length === 1 && last[0] === index) return normalized;
  const nextGroup = partitions.length;
  const moved = {
    ...normalized[index],
    group: nextGroup,
    join: "and" as const,
  };
  const rest = normalized.filter((_, i) => i !== index);
  return normalizeOrGroups([...rest, moved]);
};

// Append a clause into its own NEW group — the click-to-create path:
// the strip's "+ OR group" control arms a pending group and the panel
// inserts the next clause through here. An empty list just appends
// (one group needs no indices).
export const insertClauseInNewGroup = (
  filters: FrontendFilter[],
  clause: FrontendFilter
): FrontendFilter[] => {
  const normalized = normalizeOrGroups(normalizeJoins(filters));
  if (normalized.length === 0) return [{ ...clause, join: "and" }];
  const nextGroup = orGroupPartitions(normalized).length;
  return normalizeOrGroups([
    ...normalized,
    { ...clause, join: "and", group: nextGroup },
  ]);
};

// Insert a new clause into an EXISTING group: spliced into the group's
// same-attribute or-run when there is one, else appended to the group
// with `and`. Falls back to the default insert when the group index
// doesn't exist (e.g. the group was dissolved while the panel was open).
export const insertClauseIntoGroup = (
  filters: FrontendFilter[],
  clause: FrontendFilter,
  group: number
): FrontendFilter[] => {
  const normalized = normalizeOrGroups(normalizeJoins(filters));
  const partitions = orGroupPartitions(normalized);
  if (group < 0 || group >= partitions.length) {
    return insertClauseWithDefaultJoin(normalized, clause);
  }
  const partition = partitions[group];
  const groupValue = normalized[partition[0]].group;
  const withGroup: FrontendFilter =
    groupValue === undefined ? clause : { ...clause, group: groupValue };
  const lastSameName = partition
    .filter((i) => normalized[i].name === clause.name)
    .at(-1);
  const next = [...normalized];
  if (lastSameName === undefined) {
    next.splice(partition.at(-1)! + 1, 0, { ...withGroup, join: "and" });
  } else {
    next.splice(lastSameName + 1, 0, { ...withGroup, join: "or" });
  }
  return next;
};

// Move the clause at `index` to the end of the group `group` — dropping
// a chip onto another group's container. A move that empties the source
// group collapses it.
export const moveClauseToGroup = (
  filters: FrontendFilter[],
  index: number,
  group: number
): FrontendFilter[] => {
  const normalized = normalizeOrGroups(normalizeJoins(filters));
  if (index < 0 || index >= normalized.length) return normalized;
  if ((normalized[index].group ?? 0) === group) return normalized;
  const partitions = orGroupPartitions(normalized);
  if (group < 0 || group >= partitions.length) return normalized;
  const moved: FrontendFilter = {
    ...normalized[index],
    group,
    join: "and",
  };
  const insertAfter = partitions[group].at(-1)!;
  const next: FrontendFilter[] = [];
  normalized.forEach((f, i) => {
    if (i !== index) next.push(f);
    if (i === insertAfter) next.push(moved);
  });
  return normalizeOrGroups(next);
};

// Upgrade a legacy (join-less) list to explicit joins reproducing the
// legacy by-name grouping: clauses regroup contiguously by attribute
// name in first-encounter order, `or` within a name, `and` between
// names. AND-of-ORs is commutative, so the reorder cannot change what
// matches. A list that already carries joins is returned untouched.
//
// Precondition: a join-less list must also be group-less. The by-name
// reorder is blind to `group` and would move clauses across group
// boundaries, rebinding what an `or` attaches to. The UI never emits
// group-without-join (groups are only created by explicit-join
// mutations), so this is an invariant, not a handled case.
export const normalizeJoins = (filters: FrontendFilter[]): FrontendFilter[] => {
  if (filters.length === 0 || hasExplicitJoins(filters)) return filters;
  const order: string[] = [];
  const byName = new Map<string, FrontendFilter[]>();
  for (const f of filters) {
    if (!byName.has(f.name)) {
      order.push(f.name);
      byName.set(f.name, []);
    }
    byName.get(f.name)!.push(f);
  }
  const result: FrontendFilter[] = [];
  for (const name of order) {
    const clauses = byName.get(name)!;
    clauses.forEach((f, i) => {
      result.push({ ...f, join: i === 0 ? "and" : "or" });
    });
  }
  return result;
};

// Insert a new clause with the default connector: adjacent to the last
// same-attribute clause in the LAST group with `or` (extending that
// run), else appended to the last group with `and`. New clauses always
// land in the last group — the one the user most recently built.
// Normalizes first so the insert composes with legacy lists.
export const insertClauseWithDefaultJoin = (
  filters: FrontendFilter[],
  clause: FrontendFilter
): FrontendFilter[] => {
  const normalized = normalizeOrGroups(normalizeJoins(filters));
  const lastPartition = orGroupPartitions(normalized).at(-1);
  const lastGroup =
    lastPartition === undefined
      ? undefined
      : normalized[lastPartition[0]].group;
  const grouped = (f: FrontendFilter): FrontendFilter =>
    lastGroup === undefined ? f : { ...f, group: lastGroup };
  const lastSameName = (lastPartition ?? [])
    .filter((i) => normalized[i].name === clause.name)
    .at(-1);
  if (lastSameName === undefined) {
    return [...normalized, grouped({ ...clause, join: "and" })];
  }
  const next = [...normalized];
  next.splice(lastSameName + 1, 0, grouped({ ...clause, join: "or" }));
  return next;
};

// Flip the connector binding clause `index` to its predecessor.
// Normalizes first, so toggling on a legacy list starts from the
// grouping the user was already seeing.
export const toggleJoin = (
  filters: FrontendFilter[],
  index: number
): FrontendFilter[] => {
  const normalized = normalizeJoins(filters);
  if (index <= 0 || index >= normalized.length) return normalized;
  const next = [...normalized];
  const current = next[index];
  next[index] = { ...current, join: current.join === "or" ? "and" : "or" };
  return next;
};

// Move the clause at `from` to position `to`. The clause's join travels
// with it and it adopts the OR group of the position it lands in; the
// resulting adjacency decides the new bracketing, which the chip strip
// renders live. A move that empties a group collapses it.
export const reorderClause = (
  filters: FrontendFilter[],
  from: number,
  to: number
): FrontendFilter[] => {
  const normalized = normalizeOrGroups(normalizeJoins(filters));
  if (
    from < 0 ||
    from >= normalized.length ||
    to < 0 ||
    to >= normalized.length ||
    from === to
  ) {
    return normalized;
  }
  const next = [...normalized];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, { ...moved, group: normalized[to].group });
  return normalizeOrGroups(next);
};

// Remove the clause at `index`. Joins on the remaining clauses keep
// describing their (possibly new) predecessor; the first clause's join
// is ignored by both renderer and backend. A removal that empties an
// OR group collapses it (and a now-single group drops its indices).
// Normalizes first like every sibling mutation helper, so `index` is
// an index into the normalized list the strip renders.
export const removeClause = (
  filters: FrontendFilter[],
  index: number
): FrontendFilter[] => {
  const normalized = normalizeOrGroups(normalizeJoins(filters));
  return normalizeOrGroups(normalized.filter((_, i) => i !== index));
};

// Drop every clause `keep` refuses (a deleted column's) without widening
// what the rest match. A dropped clause that started a run (its join is
// not "or") hands its join to the next kept clause when that one is an
// "or" continuation: `A AND (X OR B)` without X is `A AND B`, not
// `A OR B`. The server applies the same rule to saved views.
export const dropClauses = (
  filters: FrontendFilter[],
  keep: (clause: FrontendFilter) => boolean
): FrontendFilter[] => {
  const result: FrontendFilter[] = [];
  let pending: { join: FrontendFilter["join"] } | null = null;
  for (const clause of filters) {
    if (!keep(clause)) {
      if (clause.join !== "or") pending = { join: clause.join };
      continue;
    }
    if (pending && clause.join === "or") {
      const { join: _join, ...rest } = clause;
      void _join;
      result.push(
        pending.join === undefined ? rest : { ...rest, join: pending.join }
      );
    } else {
      result.push(clause);
    }
    pending = null;
  }
  return result;
};

// The contiguous OR groups a clause list folds into — the bracketing the
// chip strip renders. Each group is a list of indexes into `filters`.
// Legacy (join-less) lists group by attribute name, matching what the
// backend does with the same payload.
export const joinGroups = (filters: FrontendFilter[]): number[][] => {
  const groups: number[][] = [];
  if (hasExplicitJoins(filters)) {
    filters.forEach((f, i) => {
      if (i > 0 && f.join === "or" && groups.length > 0) {
        groups[groups.length - 1].push(i);
      } else {
        groups.push([i]);
      }
    });
  } else {
    const groupIndexByName = new Map<string, number>();
    filters.forEach((f, i) => {
      const g = groupIndexByName.get(f.name);
      if (g === undefined) {
        groupIndexByName.set(f.name, groups.length);
        groups.push([i]);
      } else {
        groups[g].push(i);
      }
    });
  }
  return groups;
};
